import { RiAddLine, RiCloseLine, RiDeleteBinLine } from 'react-icons/ri';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type JSX,
} from 'react';
import {
  effectiveTierValueType,
  needsCouponDiscount,
  needsOfferCopyLine,
  needsTextDiscount,
  needsTierRules,
  tierTemplateExampleValue,
} from '@/components/ui/OfferProductPreview';
import {
  createBrandOffer,
  patchBrandOffer,
} from '@/services/slaydAPI/brandOffers';
import useBrandOfferStore from '@/stores/brandOfferStore';
import useToastStore from '@/stores/ToastStore';
import type {
  BrandOfferFormData,
  DiscountConfig,
  OfferType,
  TierRule,
} from '@/types/BrandOffer.types';
import {
  OFFER_TYPE_LABELS,
  OFFER_TYPES,
  TIER_OFFER_TEMPLATES,
} from '../BrandOffersPage.constants';
import type {
  BrandOfferFormPillProps,
  BrandOfferFormProps,
} from '../BrandOffersPage.types';
import {
  BRAND_OFFER_FORM_COPY,
  DEFAULT_PREVIEW_BRAND,
  formatTierTemplateExample,
  removeTierRuleAriaLabel,
} from './BrandOfferForm.constants';
import styles from './BrandOfferForm.module.css';
import {
  allowsShowTimerToggle,
  attachSuffixSymbol,
  buildDefault,
  defaultsForType,
  formDataFromDuplicateSource,
  initialDiscountForType,
  normalizeBrandOfferRecord,
  offerToForm,
  requiresOnSale,
  stripSuffixSymbol,
  swapSuffixSymbol,
  validate,
} from './BrandOfferForm.utils';
import BrandScopeDropdown from './BrandScopeDropdown';
import OfferFeedPreview from './OfferFeedPreview';

const Pill = ({
  active,
  variant = 'block',
  role,
  onClick,
  children,
}: BrandOfferFormPillProps): JSX.Element => {
  const base = variant === 'block' ? styles.offerTypePill : styles.inlinePill;
  const activeClass =
    variant === 'block' ? styles.offerTypePillActive : styles.inlinePillActive;
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'radio' ? active : undefined}
      className={active ? `${base} ${activeClass}` : base}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const BrandOfferForm = ({
  offer,
  duplicateSource = null,
  onClose,
  onSaved,
}: BrandOfferFormProps): JSX.Element => {
  const { brands } = useBrandOfferStore();
  const { showToast } = useToastStore();
  const [formData, setFormData] = useState<BrandOfferFormData>(
    offer ? offerToForm(offer) : buildDefault()
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect((): void => {
    setFormData(
      offer
        ? offerToForm(offer)
        : duplicateSource
          ? formDataFromDuplicateSource(duplicateSource)
          : buildDefault()
    );
    setErrors([]);
  }, [offer, duplicateSource]);

  useEffect((): void => {
    if (offer || duplicateSource) return;
    const firstBrand = brands[0];
    if (!firstBrand) return;
    setFormData((prev) =>
      prev.brand == null ? { ...prev, brand: firstBrand.id } : prev
    );
  }, [offer, duplicateSource, brands]);

  useEffect((): void => {
    if (!requiresOnSale(formData.offerType)) return;
    if (formData.isSale) return;
    setFormData((prev) => ({ ...prev, isSale: true }));
  }, [formData.offerType, formData.isSale]);

  const previewBrandName = useMemo((): string => {
    if (formData.brand == null) return DEFAULT_PREVIEW_BRAND;
    return (
      brands.find((b) => b.id === formData.brand)?.name ??
      BRAND_OFFER_FORM_COPY.previewBrandFallback
    );
  }, [formData.brand, brands]);

  const setField = useCallback(
    <K extends keyof BrandOfferFormData>(
      field: K,
      value: BrandOfferFormData[K]
    ): void => setFormData((prev) => ({ ...prev, [field]: value })),
    []
  );

  const patchDiscount = useCallback((patch: Partial<DiscountConfig>): void => {
    setFormData((prev) => ({
      ...prev,
      discountConfig: {
        ...(prev.discountConfig ?? { type: 'FLAT' }),
        ...patch,
      },
    }));
  }, []);

  /** Switch FLAT ↔ PERCENT, clearing value and re-decorating offerText's symbol. */
  const handleDiscountTypeChange = useCallback(
    (nextType: 'FLAT' | 'PERCENT'): void => {
      setFormData((prev) => {
        const prevConfig = prev.discountConfig ?? { type: 'FLAT' };
        const prevType =
          prevConfig.type === 'FLAT' || prevConfig.type === 'PERCENT'
            ? prevConfig.type
            : undefined;
        const nextOfferText = swapSuffixSymbol(
          String(prevConfig.offerText ?? ''),
          prevType,
          nextType
        );
        return {
          ...prev,
          discountConfig: {
            ...prevConfig,
            type: nextType,
            value: null,
            offerText: nextOfferText,
          },
        };
      });
    },
    []
  );

  const handleOfferTypeChange = useCallback((type: OfferType): void => {
    setFormData((prev) => {
      const typeDefaults = defaultsForType(type);
      return {
        ...prev,
        offerType: type,
        name: prev.name,
        startAt: prev.startAt,
        endsAt: prev.endsAt,
        isSale: typeDefaults.isSale,
        showTimer: typeDefaults.showTimer,
        discountConfig: initialDiscountForType(type),
      };
    });
  }, []);

  /** Single helper for any tier-rules mutation; keeps offerText in sync. */
  const mutateTiers = useCallback(
    (fn: (rules: TierRule[]) => TierRule[]): void => {
      setFormData((prev) => ({
        ...prev,
        discountConfig: {
          type: 'TIER',
          rules: fn(prev.discountConfig?.rules ?? []),
          offerText: prev.discountConfig?.offerText ?? '',
        },
      }));
    },
    []
  );

  const addTierRule = useCallback((): void => {
    mutateTiers((rules) => {
      const lastMax = rules[rules.length - 1]?.max ?? 0;
      return [
        ...rules,
        {
          min: lastMax,
          max: null,
          value: 0,
          tierValueType: 'FLAT',
        },
      ];
    });
  }, [mutateTiers]);

  const updateTierRule = useCallback(
    (index: number, patch: Partial<TierRule>): void => {
      mutateTiers((rules) =>
        rules.map((r, i) => (i === index ? { ...r, ...patch } : r))
      );
    },
    [mutateTiers]
  );

  const removeTierRule = useCallback(
    (index: number): void => {
      mutateTiers((rules) => rules.filter((_, i) => i !== index));
    },
    [mutateTiers]
  );

  const handleBrandChange = useCallback(
    (id: number | null): void => setField('brand', id),
    [setField]
  );

  const handleSubmit = async (): Promise<void> => {
    const errs = validate(formData);
    if (errs.length) {
      setErrors(errs);
      showToast(errs.join(' '));
      return;
    }
    setErrors([]);
    setIsSubmitting(true);

    const timerAllowed = allowsShowTimerToggle(formData.offerType);
    const payload: BrandOfferFormData = {
      ...formData,
      name: formData.name.trim(),
      isSale: requiresOnSale(formData.offerType) ? true : formData.isSale,
      showTimer: timerAllowed ? formData.showTimer : false,
      startAt: formData.startAt
        ? new Date(formData.startAt).toISOString()
        : null,
      endsAt: formData.endsAt ? new Date(formData.endsAt).toISOString() : null,
      discountConfig:
        formData.discountConfig?.type === 'TIER'
          ? {
              ...formData.discountConfig,
              rules: formData.discountConfig.rules?.map((rule) => {
                const kind =
                  rule.tierValueType ??
                  effectiveTierValueType(rule, formData.offerType);
                return {
                  ...rule,
                  tierValueType: kind,
                  max: rule.max === null ? 9999999 : rule.max,
                };
              }),
            }
          : formData.discountConfig,
    };

    try {
      const saved = offer
        ? await patchBrandOffer(offer.id, payload)
        : await createBrandOffer(payload);
      showToast(
        offer
          ? BRAND_OFFER_FORM_COPY.toastUpdated
          : BRAND_OFFER_FORM_COPY.toastPublished
      );
      onSaved(normalizeBrandOfferRecord(saved));
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : BRAND_OFFER_FORM_COPY.toastSaveFailed;
      setErrors([msg]);
      showToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tiers = formData.discountConfig?.rules ?? [];
  const discType = formData.discountConfig?.type ?? 'FLAT';
  const offerText = String(formData.discountConfig?.offerText ?? '');
  const saleLocked = requiresOnSale(formData.offerType);

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.drawer}>
        <header className={styles.drawerHeader}>
          <div className={styles.drawerHeaderMain}>
            <p className={styles.drawerEyebrow}>
              {offer
                ? BRAND_OFFER_FORM_COPY.drawerEyebrowEdit
                : BRAND_OFFER_FORM_COPY.drawerEyebrowCreate}
            </p>
            <h2 className={styles.drawerTitle}>
              {offer
                ? BRAND_OFFER_FORM_COPY.drawerTitleEdit
                : BRAND_OFFER_FORM_COPY.drawerTitleCreate}
            </h2>
            <p className={styles.drawerSubtitle}>
              {BRAND_OFFER_FORM_COPY.drawerSubtitle}
            </p>
          </div>
          <button
            type="button"
            aria-label={BRAND_OFFER_FORM_COPY.closeDrawerAria}
            className={styles.closeBtn}
            onClick={onClose}
          >
            <RiCloseLine size={22} />
          </button>
        </header>

        <div className={styles.drawerBody}>
          <div className={styles.bodyLayout}>
            <div className={styles.formColumn}>
              {errors.length > 0 && (
                <div className={styles.errorBox} role="alert">
                  {errors.map((e, i) => (
                    <p key={i}>{e}</p>
                  ))}
                </div>
              )}

              <section className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  {BRAND_OFFER_FORM_COPY.sectionIdentity}
                </h3>
                <div className={styles.formSectionBody}>
                  <div className={styles.offerTypesBlock}>
                    <p className={styles.offerTypesLabel}>
                      {BRAND_OFFER_FORM_COPY.offerTypesLabel}
                    </p>
                    <div
                      className={styles.offerTypePills}
                      role="radiogroup"
                      aria-label={
                        BRAND_OFFER_FORM_COPY.offerTypesRadiogroupAria
                      }
                    >
                      {OFFER_TYPES.map((t) => (
                        <Pill
                          key={t}
                          role="radio"
                          active={formData.offerType === t}
                          onClick={() => handleOfferTypeChange(t)}
                        >
                          {OFFER_TYPE_LABELS[t]}
                        </Pill>
                      ))}
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="offer-name">
                      {BRAND_OFFER_FORM_COPY.offerNameLabel}
                    </label>
                    <input
                      id="offer-name"
                      className={styles.input}
                      type="text"
                      placeholder={BRAND_OFFER_FORM_COPY.offerNamePlaceholder}
                      value={formData.name}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setField('name', e.target.value)
                      }
                    />
                  </div>

                  <div className={styles.row}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="offer-priority">
                        {BRAND_OFFER_FORM_COPY.rankLabel}
                      </label>
                      <input
                        id="offer-priority"
                        className={styles.input}
                        type="number"
                        placeholder={BRAND_OFFER_FORM_COPY.rankPlaceholder}
                        value={formData.priority || ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setField('priority', Number(e.target.value))
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="offer-brand">
                        {BRAND_OFFER_FORM_COPY.brandLabel}
                      </label>
                      {brands.length > 0 ? (
                        <BrandScopeDropdown
                          brands={brands}
                          value={formData.brand}
                          onChange={handleBrandChange}
                        />
                      ) : (
                        <input
                          id="offer-brand"
                          className={styles.input}
                          type="number"
                          placeholder={BRAND_OFFER_FORM_COPY.brandIdPlaceholder}
                          value={formData.brand ?? ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setField(
                              'brand',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  {BRAND_OFFER_FORM_COPY.sectionTimeWindow}
                </h3>
                <div className={styles.formSectionBody}>
                  <div className={styles.timeFieldsRow}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="offer-starts-at">
                        {BRAND_OFFER_FORM_COPY.startsAtLabel}
                      </label>
                      <input
                        id="offer-starts-at"
                        className={styles.input}
                        type="datetime-local"
                        value={formData.startAt ?? ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setField('startAt', e.target.value || null)
                        }
                      />
                      <p className={styles.hintInline}>
                        {BRAND_OFFER_FORM_COPY.startsAtHint}
                      </p>
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="offer-ends-at">
                        {BRAND_OFFER_FORM_COPY.endsAtLabel}
                      </label>
                      <input
                        id="offer-ends-at"
                        className={styles.input}
                        type="datetime-local"
                        value={formData.endsAt ?? ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setField('endsAt', e.target.value || null)
                        }
                      />
                    </div>
                  </div>
                </div>
              </section>

              {needsCouponDiscount(formData.offerType) && (
                <section className={styles.formSection}>
                  <h3 className={styles.sectionTitle}>
                    {BRAND_OFFER_FORM_COPY.sectionDiscount}
                  </h3>
                  <div className={styles.formSectionBody}>
                    <div className={styles.discountBlock}>
                      <div className={styles.field}>
                        <span className={styles.label}>
                          {BRAND_OFFER_FORM_COPY.discountTypeLabel}
                        </span>
                        <div
                          className={styles.inlinePills}
                          role="group"
                          aria-label={
                            BRAND_OFFER_FORM_COPY.discountTypeGroupAria
                          }
                        >
                          <Pill
                            variant="inline"
                            active={discType === 'FLAT'}
                            onClick={() => handleDiscountTypeChange('FLAT')}
                          >
                            {BRAND_OFFER_FORM_COPY.discountFlatPill}
                          </Pill>
                          <Pill
                            variant="inline"
                            active={discType === 'PERCENT'}
                            onClick={() => handleDiscountTypeChange('PERCENT')}
                          >
                            {BRAND_OFFER_FORM_COPY.discountPercentPill}
                          </Pill>
                        </div>
                      </div>
                      <div className={styles.field}>
                        <label
                          className={styles.label}
                          htmlFor="discount-value"
                        >
                          {BRAND_OFFER_FORM_COPY.discountValueLabel}{' '}
                          <span className={styles.hint}>
                            {discType === 'PERCENT'
                              ? BRAND_OFFER_FORM_COPY.discountValueHintPercent
                              : BRAND_OFFER_FORM_COPY.discountValueHintInr}
                          </span>
                        </label>
                        <div className={styles.inputWithSuffix}>
                          {discType === 'FLAT' && (
                            <span
                              aria-hidden
                              className={styles.discountLeadSymbol}
                            >
                              ₹
                            </span>
                          )}
                          <input
                            id="discount-value"
                            className={`${styles.input} ${styles.inputInSuffixGroup}`}
                            type="number"
                            min={0}
                            max={discType === 'PERCENT' ? 100 : undefined}
                            placeholder={
                              discType === 'PERCENT'
                                ? BRAND_OFFER_FORM_COPY.discountValuePlaceholderPercent
                                : BRAND_OFFER_FORM_COPY.discountValuePlaceholderFlat
                            }
                            value={formData.discountConfig?.value ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              patchDiscount({
                                value: e.target.value
                                  ? Number(e.target.value)
                                  : null,
                              })
                            }
                          />
                          {discType === 'PERCENT' && (
                            <span
                              aria-hidden
                              className={styles.discountTrailSymbol}
                            >
                              %
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {needsTextDiscount(formData.offerType) && (
                <section className={styles.formSection}>
                  <h3 className={styles.sectionTitle}>
                    {BRAND_OFFER_FORM_COPY.sectionOnTileCopy}
                  </h3>
                  <div className={styles.formSectionBody}>
                    <div className={styles.field}>
                      <label className={styles.label} htmlFor="discount-text">
                        {BRAND_OFFER_FORM_COPY.discountTextLabel}
                      </label>
                      <textarea
                        id="discount-text"
                        className={styles.textarea}
                        rows={2}
                        placeholder={
                          BRAND_OFFER_FORM_COPY.discountTextPlaceholder
                        }
                        value={offerText}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          patchDiscount({
                            type: 'TEXT',
                            value: null,
                            offerText: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </section>
              )}

              {needsOfferCopyLine(formData.offerType) &&
                !needsTierRules(formData.offerType) &&
                (() => {
                  const symbolType: 'FLAT' | 'PERCENT' =
                    discType === 'PERCENT' ? 'PERCENT' : 'FLAT';
                  return (
                    <section className={styles.formSection}>
                      <h3 className={styles.sectionTitle}>
                        {BRAND_OFFER_FORM_COPY.sectionShopperLine}
                      </h3>
                      <div className={styles.formSectionBody}>
                        <div className={styles.field}>
                          <label
                            className={styles.label}
                            htmlFor="offer-copy-coupon"
                          >
                            {BRAND_OFFER_FORM_COPY.suffixTextLabel}
                          </label>
                          <textarea
                            id="offer-copy-coupon"
                            className={styles.textarea}
                            rows={3}
                            placeholder={
                              BRAND_OFFER_FORM_COPY.suffixTextPlaceholder
                            }
                            value={offerText}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                              const stripped = stripSuffixSymbol(
                                e.target.value,
                                symbolType
                              );
                              patchDiscount({
                                offerText: attachSuffixSymbol(
                                  stripped,
                                  symbolType
                                ),
                              });
                            }}
                          />
                          <p className={styles.hintInline}>
                            {BRAND_OFFER_FORM_COPY.shopperLineHintBeforeStrong}
                            <strong>
                              {BRAND_OFFER_FORM_COPY.shopperLineHintStrong}
                            </strong>
                            {BRAND_OFFER_FORM_COPY.shopperLineHintAfterStrong}
                            {BRAND_OFFER_FORM_COPY.shopperLineHintToken}
                            {BRAND_OFFER_FORM_COPY.shopperLineHintTail}
                          </p>
                        </div>
                      </div>
                    </section>
                  );
                })()}

              {needsTierRules(formData.offerType) && (
                <section className={styles.formSection}>
                  <h3 className={styles.sectionTitle}>
                    {BRAND_OFFER_FORM_COPY.sectionCartTiers}
                  </h3>
                  <div className={styles.formSectionBody}>
                    <div className={styles.field}>
                      <div className={styles.tierHeader}>
                        <span className={styles.hintInline}>
                          {BRAND_OFFER_FORM_COPY.tierHintPerTierType}
                        </span>
                        <button
                          type="button"
                          className={styles.addRuleBtn}
                          onClick={addTierRule}
                        >
                          <RiAddLine size={14} />{' '}
                          {BRAND_OFFER_FORM_COPY.addTier}
                        </button>
                      </div>
                      <div className={styles.tierTable}>
                        <div className={styles.tierHead}>
                          <span>{BRAND_OFFER_FORM_COPY.tierHeadMin}</span>
                          <span>{BRAND_OFFER_FORM_COPY.tierHeadMax}</span>
                          <span>
                            {BRAND_OFFER_FORM_COPY.tierHeadDiscountType}
                          </span>
                          <span>{BRAND_OFFER_FORM_COPY.tierHeadAmount}</span>
                          <span />
                        </div>
                        {tiers.map((rule, i) => {
                          const rowKind = effectiveTierValueType(
                            rule,
                            formData.offerType
                          );
                          const groupAriaLabel = `${BRAND_OFFER_FORM_COPY.tierDiscountTypeGroupAria} ${i + 1}`;
                          return (
                            <div key={i} className={styles.tierRow}>
                              <div className={styles.tierRowMinMax}>
                                <input
                                  className={styles.tierInput}
                                  type="number"
                                  min={0}
                                  placeholder={
                                    BRAND_OFFER_FORM_COPY.tierPlaceholderMin
                                  }
                                  value={rule.min || ''}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                  ) =>
                                    updateTierRule(i, {
                                      min: Number(e.target.value),
                                    })
                                  }
                                />
                                <input
                                  className={styles.tierInput}
                                  type="number"
                                  min={0}
                                  placeholder={
                                    BRAND_OFFER_FORM_COPY.tierPlaceholderMax
                                  }
                                  value={rule.max ?? ''}
                                  onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                  ) =>
                                    updateTierRule(i, {
                                      max: e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                    })
                                  }
                                />
                              </div>
                              <div
                                className={styles.tierTypePicks}
                                role="group"
                                aria-label={groupAriaLabel}
                              >
                                <button
                                  type="button"
                                  className={
                                    rowKind === 'FLAT'
                                      ? `${styles.tierTypePick} ${styles.tierTypePickOn}`
                                      : styles.tierTypePick
                                  }
                                  onClick={() =>
                                    updateTierRule(i, {
                                      tierValueType: 'FLAT',
                                    })
                                  }
                                >
                                  ₹
                                </button>
                                <button
                                  type="button"
                                  className={
                                    rowKind === 'PERCENT'
                                      ? `${styles.tierTypePick} ${styles.tierTypePickOn}`
                                      : styles.tierTypePick
                                  }
                                  onClick={() =>
                                    updateTierRule(i, {
                                      tierValueType: 'PERCENT',
                                    })
                                  }
                                >
                                  %
                                </button>
                              </div>
                              <div className={styles.tierAmountRow}>
                                <div className={styles.tierAmountCell}>
                                  {rowKind === 'FLAT' && (
                                    <span
                                      aria-hidden
                                      className={styles.tierAmountRupee}
                                    >
                                      ₹
                                    </span>
                                  )}
                                  <input
                                    className={`${styles.tierInput} ${styles.tierAmountInput}`}
                                    type="number"
                                    min={0}
                                    max={
                                      rowKind === 'PERCENT' ? 100 : undefined
                                    }
                                    placeholder={
                                      rowKind === 'PERCENT'
                                        ? BRAND_OFFER_FORM_COPY.tierPlaceholderValuePercent
                                        : BRAND_OFFER_FORM_COPY.tierPlaceholderValueRupee
                                    }
                                    value={rule.value || ''}
                                    onChange={(
                                      e: ChangeEvent<HTMLInputElement>
                                    ) =>
                                      updateTierRule(i, {
                                        value: Number(e.target.value),
                                      })
                                    }
                                  />
                                  {rowKind === 'PERCENT' && (
                                    <span
                                      aria-hidden
                                      className={styles.tierAmountPercent}
                                    >
                                      %
                                    </span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  aria-label={removeTierRuleAriaLabel(i)}
                                  className={styles.removeRuleBtn}
                                  disabled={tiers.length === 1}
                                  onClick={() => removeTierRule(i)}
                                >
                                  <RiDeleteBinLine size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className={styles.field}>
                      <span className={styles.label}>
                        {BRAND_OFFER_FORM_COPY.bannerTemplateLabel}
                      </span>
                      <p className={styles.hintInline}>
                        {BRAND_OFFER_FORM_COPY.bannerTemplateHint1}
                        <strong>{'{value}'}</strong>
                        {BRAND_OFFER_FORM_COPY.bannerTemplateHint2}
                      </p>
                      <div className={styles.templateGrid}>
                        {TIER_OFFER_TEMPLATES.map((t) => {
                          const isSelected = offerText === t.template;
                          const exampleValue = tierTemplateExampleValue(
                            tiers,
                            formData.offerType
                          );
                          const preview = t.template.replace(
                            /\{value\}/g,
                            exampleValue
                          );
                          return (
                            <button
                              key={t.template}
                              type="button"
                              className={
                                isSelected
                                  ? `${styles.templatePill} ${styles.templatePillActive}`
                                  : styles.templatePill
                              }
                              onClick={() =>
                                patchDiscount({ offerText: t.template })
                              }
                            >
                              <span className={styles.templateLabel}>
                                {t.label}
                              </span>
                              <span className={styles.templatePreview}>
                                {formatTierTemplateExample(preview)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section className={styles.formSection}>
                <div className={styles.formSectionBody}>
                  <div
                    role="button"
                    tabIndex={saleLocked ? -1 : 0}
                    aria-disabled={saleLocked ? true : undefined}
                    className={
                      saleLocked
                        ? `${styles.toggleRow} ${styles.toggleRowLocked}`
                        : styles.toggleRow
                    }
                    onClick={() => {
                      if (saleLocked) return;
                      setField('isSale', !formData.isSale);
                    }}
                    onKeyDown={(e) => {
                      if (saleLocked) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setField('isSale', !formData.isSale);
                      }
                    }}
                  >
                    <div>
                      <p className={styles.toggleLabel}>
                        {BRAND_OFFER_FORM_COPY.onSaleLabel}
                      </p>
                      <p className={styles.toggleHint}>
                        {saleLocked
                          ? BRAND_OFFER_FORM_COPY.onSaleHintLocked
                          : BRAND_OFFER_FORM_COPY.onSaleHintOptional}
                      </p>
                    </div>
                    <div
                      className={
                        formData.isSale
                          ? `${styles.toggle} ${styles.toggleOn}`
                          : styles.toggle
                      }
                    >
                      <span className={styles.toggleThumb} />
                    </div>
                  </div>

                  {allowsShowTimerToggle(formData.offerType) && (
                    <div
                      role="button"
                      tabIndex={0}
                      className={styles.toggleRow}
                      onClick={() => setField('showTimer', !formData.showTimer)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setField('showTimer', !formData.showTimer);
                        }
                      }}
                    >
                      <div>
                        <p className={styles.toggleLabel}>
                          {BRAND_OFFER_FORM_COPY.showTimerLabel}
                        </p>
                        <p className={styles.toggleHint}>
                          {BRAND_OFFER_FORM_COPY.showTimerHint}
                        </p>
                      </div>
                      <div
                        className={
                          formData.showTimer
                            ? `${styles.toggle} ${styles.toggleOn}`
                            : styles.toggle
                        }
                      >
                        <span className={styles.toggleThumb} />
                      </div>
                    </div>
                  )}

                  <div
                    role="button"
                    tabIndex={0}
                    className={styles.toggleRow}
                    onClick={() => setField('isActive', !formData.isActive)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setField('isActive', !formData.isActive);
                      }
                    }}
                  >
                    <div>
                      <p className={styles.toggleLabel}>
                        {BRAND_OFFER_FORM_COPY.liveOnAppLabel}
                      </p>
                      <p className={styles.toggleHint}>
                        {BRAND_OFFER_FORM_COPY.liveOnAppHint}
                      </p>
                    </div>
                    <div
                      className={
                        formData.isActive
                          ? `${styles.toggle} ${styles.toggleOn}`
                          : styles.toggle
                      }
                    >
                      <span className={styles.toggleThumb} />
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <OfferFeedPreview data={formData} brandName={previewBrandName} />
          </div>
        </div>

        <footer className={styles.drawerFooter}>
          <button
            type="button"
            className={styles.cancelBtn}
            disabled={isSubmitting}
            onClick={onClose}
          >
            {BRAND_OFFER_FORM_COPY.footerDiscard}
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting
              ? BRAND_OFFER_FORM_COPY.footerSaving
              : offer
                ? BRAND_OFFER_FORM_COPY.footerSaveUpdates
                : BRAND_OFFER_FORM_COPY.footerPublish}
          </button>
        </footer>
      </div>
    </>
  );
};

export default BrandOfferForm;
